import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';

import classNames from 'classnames';
import pluralize from 'pluralize';
import { faBoxArchive } from '@fortawesome/pro-solid-svg-icons';
import { faCirclePlus } from '@fortawesome/pro-regular-svg-icons';

import { connect } from 'react-redux';
import { deletePlan, updatePlan } from './store/plans/actions';

import { Button } from 'components/ui/button';
import ArchiveModal from './archive_modal';
import Dropdown from 'components/dropdown';
import MultiselectModal from 'components/multiselect_modal';
import PlanModal from './form/modal';
import PlanTiersTable from './plan_tiers_table';
import Tooltip from 'components/tooltip';
import { formatDate, MONTH_DAY_FORMAT } from 'helpers/formatters/date_time_formats';
import { showRequestFailure } from 'components/ui/snackbar/helper';
import { getPlanTypeLabel } from 'bundles/backoffice/helpers/plans';

export function PlanRow({ deletePlan, plan, updatePlan, users }) {
  const planNameRef = React.createRef();

  const [showUsersModal, setShowUsersModal] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const savingUsers = useRef(false);

  // The picker closes once the server has saved the users; on a failure it keeps the picks to try again.
  async function handleUsersChange(users) {
    if (savingUsers.current) return;

    savingUsers.current = true;
    try {
      await updatePlan(plan, { users });
      setShowUsersModal(false);
    } catch (errors) {
      showRequestFailure(errors, "The plan's users could not be saved. Please try again.");
    } finally {
      savingUsers.current = false;
    }
  }

  async function handleDelete() {
    try {
      await deletePlan(plan);
    } catch (errors) {
      showRequestFailure(errors, 'The plan could not be deleted. Please try again.');
    }
  }

  // Archived users are already excluded from this collection
  const nonArchivedUserIds = users.map(user => user.id);
  const visiblePlanUsers = plan.users.filter(user => nonArchivedUserIds.includes(user.id));

  return <>
    <tr className={classNames({ 'text-muted': plan.archived })}>
      <td>
        <Tooltip childrenRef={planNameRef} content={plan.name} onlyOnOverflow>
          <a
            className={classNames('clickable', { 'text-muted': plan.archived })}
            ref={planNameRef}
            onClick={() => setShowPlanModal(true)}
          >
            {plan.name}
          </a>
        </Tooltip>
      </td>
      <td>{getPlanTypeLabel(plan.realCalculationMode)}</td>
      <td className="start-date-cell">{formatDate(plan.startDate, MONTH_DAY_FORMAT)}</td>
      <td className="reset-period-cell">{pluralize('Year', plan.resetPeriod, true)}</td>
      <td className="next-period-cell">{formatDate(plan.nextPeriodStarts)}</td>
      <td className="tiers-cell">
        <Tooltip
          content={<PlanTiersTable tiers={plan.tiers} />}
          overlayClassName="plan-tiers-popover"
          trigger="focus"
        >
          <a
            className={classNames('clickable', { 'text-muted': plan.archived })}
            href=""
            onClick={e => e.preventDefault()}
          >
            {pluralize('Tier', plan.tiers.length, true)}
          </a>
        </Tooltip>
      </td>
      <td>
        {!plan.archived &&
          <div className="clickable" onClick={() => setShowUsersModal(true)}>
            {visiblePlanUsers.length == 0
              ? <Button icon={faCirclePlus} label="Add Assignee" size="sm" variant="ghost" />
              : <a className="clickable">{pluralize('Assignee', visiblePlanUsers.length, true)}</a>
            }
          </div>
        }
        <MultiselectModal
          closeOnSave={false}
          optionLabelName="name"
          options={users}
          optionsLabel="Users"
          optionValueName="id"
          show={showUsersModal}
          title="Assign Users"
          value={plan.users}
          onClose={() => setShowUsersModal(false)}
          onSave={handleUsersChange}
        />
      </td>
      {plan.archived ? <td className="sticky-cell end-cell" /> :
        <Dropdown.Cell>
          <Dropdown.Item
            isEdit
            onClick={() => setShowPlanModal(true)}
          />
          <Dropdown.Item
            disabled={plan.payablesCount > 0}
            disabledMessage="You cannot delete a plan that has been used on a payable."
            isDelete
            onClick={handleDelete}
          />
          <Dropdown.Item
            icon={faBoxArchive}
            label="Archive"
            onClick={() => setShowArchiveModal(true)}
          />
        </Dropdown.Cell>
      }
    </tr>

    <PlanModal
      plan={plan}
      show={showPlanModal}
      onClose={() => setShowPlanModal(false)}
    />

    <ArchiveModal
      plan={plan}
      show={showArchiveModal}
      onHide={() => setShowArchiveModal(false)}
    />
  </>;
}

PlanRow.propTypes = {
  deletePlan: PropTypes.func.isRequired,
  plan: PropTypes.shape({
    archived: PropTypes.bool,
    calculationMode: PropTypes.string,
    id: PropTypes.number,
    name: PropTypes.string,
    resetPeriod: PropTypes.number,
    startDate: PropTypes.string,
    nextPeriodStarts: PropTypes.string,
    tiers: PropTypes.array.isRequired,
    users: PropTypes.array.isRequired,
  }).isRequired,
  updatePlan: PropTypes.func.isRequired,
  users: PropTypes.array.isRequired,
};

const mapStateToProps = state => ({
  users: state.users,
});

const mapDispatchToProps = {
  deletePlan,
  updatePlan,
};

export default connect(mapStateToProps, mapDispatchToProps)(PlanRow);
